import env from '@/lib/env';

/**
 * Per-domain mock flags for the registered-student prototype.
 *
 * Each flag flips one data domain between its mock adapter and the real API.
 * Default: mocks ON (this is a prototype). Set `VITE_MOCK_<DOMAIN>=false` once the
 * real API for that domain ships — no UI change is required.
 *
 * See: docs/impl-plans/registered-student-frontend-prototype.md
 *      (Marking & finding mock implementations).
 */
export type MockDomain =
  | 'EXAM_TARGETS'
  | 'MATCH'
  | 'WISHLIST'
  | 'GOAL_HISTORY'
  | 'ENRICHMENT'
  | 'RELIABILITY'
  | 'EVENTS'
  | 'GROUP_CLASSES'
  | 'ACCOUNT_SETTINGS';

function resolve(domain: MockDomain): boolean {
  const v = env.get(`VITE_MOCK_${domain}`);
  // Default ON in the prototype unless explicitly disabled.
  return v === undefined ? true : v !== 'false';
}

export const mockFlags: Record<MockDomain, boolean> = {
  EXAM_TARGETS: resolve('EXAM_TARGETS'),
  MATCH: resolve('MATCH'),
  WISHLIST: resolve('WISHLIST'),
  GOAL_HISTORY: resolve('GOAL_HISTORY'),
  ENRICHMENT: resolve('ENRICHMENT'),
  RELIABILITY: resolve('RELIABILITY'),
  EVENTS: resolve('EVENTS'),
  GROUP_CLASSES: resolve('GROUP_CLASSES'),
  ACCOUNT_SETTINGS: resolve('ACCOUNT_SETTINGS'),
};

export function isMocked(domain: MockDomain): boolean {
  return mockFlags[domain];
}

/**
 * Match-score UI kill switch (ADR-105).
 *
 * The per-offering / per-teacher match score and its "Why this match?" breakdown
 * are hidden from the marketplace UI while the scoring computation is being
 * reworked. The backend still computes and returns the score — only its display
 * is gated here — so re-enabling is a one-line flip once the recompute ships.
 *
 * Deliberately a hard-coded `false`, NOT an env/ops toggle: the score is known to
 * be wrong, so it must not be flippable on in any environment without a code change.
 *
 * See: docs/decisions/105-match-score-ui-hidden-pending-recompute.md
 */
export const MATCH_SCORE_UI_ENABLED: boolean = false;

export function activeMocks(): MockDomain[] {
  return (Object.keys(mockFlags) as MockDomain[]).filter((d) => mockFlags[d]);
}

/**
 * Release-flag registry (SCRUM-277 and beyond).
 *
 * Unlike the per-domain mock flags above (default ON), release flags default
 * OFF unless explicitly enabled — a new feature must be turned on deliberately
 * per environment, not accidentally shipped by omission.
 *
 * See: docs/impl-plans/ratings-and-reviews.md
 *      docs/decisions/118-frontend-release-flag-extends-existing-registry.md
 */
export type ReleaseFlag =
  | 'TEACHER_TIER_BENEFITS'
  | 'CLASS_MANAGEMENT'
  /**
   * Teacher Platinum free trial. Registered here so the name is typed and the flag is emitted
   * in every environment -- but note the trial UI deliberately does NOT gate on it. The backend
   * never reads a release flag when resolving a trial, so the dashboard countdown and the
   * subscription page's trial state follow server truth (`trial.active`) instead; gating them on
   * the flag would show a teacher with a live trial a "Subscribe" button that 409s. The feature
   * is inert before launch because no teacher has a trial then.
   */
  | 'TEACHER_PLATINUM_TRIAL'
  /**
   * Teacher subscription: auto-renew as explicit consent (ADR-151 / ADR-152).
   *
   * Gates the checkout consent dialog, the "Renews {date}" / "Ends {date}" renewal state and the
   * one-way turn-off action. OFF reproduces today's behaviour exactly -- a bare Subscribe button
   * that always creates a Razorpay mandate, and the legacy "Auto-renew: On/Off" cell. The backend
   * gates the matching POST /auto-renew/off endpoint on the same flag, so the UI must not offer
   * the action while it is off.
   */
  | 'TEACHER_SUBSCRIPTION_AUTO_RENEW_CHOICE'
  | 'SEO_PANEL'
  /**
   * Registration date-of-birth field (RegisterPage, student + teacher).
   * ON: DOB shown, required, validated, under-13 routed to /age-gate, `birthdate` sent to Cognito.
   * OFF: DOB not rendered, not validated, `birthdate` omitted from signUp -- so the DPDP-03
   * under-13 consent gate does NOT run from the registration form.
   * Type: release, kept long-term as a per-environment control -- NO removal date (deliberate
   * exception, ADR-162). Default if missing: OFF. Owner: product owner / engineering lead.
   * Launch: dev ON, staging OFF, prod OFF. Mobile mirror: REGISTRATION_DOB_ENABLED.
   * See docs/decisions/162-registration-dob-flag-off-in-prod-disables-age-gate.md and
   *     docs/requirements/registration-dob-feature-flag.md (bmc workspace repo).
   */
  | 'REGISTRATION_DOB';

function resolveReleaseFlag(flag: ReleaseFlag): boolean {
  const v = env.get(`VITE_FEATURE_${flag}`);
  // Default OFF unless explicitly enabled.
  return v === 'true';
}

export function isReleaseFlagOn(flag: ReleaseFlag): boolean {
  return resolveReleaseFlag(flag);
}
